import type { FC, PropsWithChildren } from 'react'
import { useState } from 'react'

import styled from '@emotion/styled'

import ColumnTrack from '@segment/matcha/components/ColumnTrack'
import Typography from '@segment/matcha/components/Typography'
import Badge from '@segment/matcha/components/Badge'
import ButtonText from '@segment/matcha/components/ButtonText'
import { ChevronRightIcon } from '@segment/matcha/components/Icons/Common'
import Media from '@segment/matcha/components/Media'
import Image from '@segment/matcha/components/Image'

import CopyTextBase, * as CopyText from '@segment/matcha/recipes/CopyText'
import SectionBase, * as Section from '@segment/matcha/recipes/Section'

import CardBase, * as Card from '@segment/matcha/recipes/Card'

import contentfulImageLoader from '@segment/utils/contentfulImageLoader/contentfulImageLoader'

import type { Startups } from '../../data'

const copy = {
  heading: `Get exclusive deals on your favorite tools`,
  description: `Focus on building your product. We’ll get you a sweet discount on all of the best marketing and analytics tools that work with Segment.`,
}

const descriptionLink = {
  text: `Submit a deal`,
  url: `https://airtable.com/shr84MIvVo4k8xbaO`,
}

const captionDescription = (
  <>
    Want to offer your product to thousands of startups?{` `}
    <a href={descriptionLink.url}>{descriptionLink.text}</a>.
  </>
)

interface GetExclusiveDealsProps {
  deals: Startups[`deals`]
}

const GetExclusiveDeals: FC<PropsWithChildren<GetExclusiveDealsProps>> = ({ deals }) => {
  const [seeMore, setSeeMore] = useState(false)

  return (
    <Wrapper>
      <StyledSection variant="centered">
        <Section.Copy>
          <CopyTextBase centered={true}>
            <CopyText.Heading tag="h2">{copy.heading}</CopyText.Heading>
            <CopyText.Description>{copy.description}</CopyText.Description>
          </CopyTextBase>
        </Section.Copy>
        <Section.Contents>
          <Body>
            <ColumnTrack columns={1} overrides={{ sm: 2, md: 3 }}>
              {deals.map((card) => (
                <CardBase key={card.key} tag="div">
                  <Card.Body>
                    <Card.Accessories>
                      <Card.AccessoriesRow>
                        <Logo
                          loader={() =>
                            contentfulImageLoader({
                              src: card.logo,
                              width: Number(card.logoDimensions.width),
                            })
                          }
                          width={card.logoDimensions.width}
                          height={card.logoDimensions.height}
                          src={card.logo}
                          alt={card.title}
                        />
                      </Card.AccessoriesRow>
                      <Card.AccessoriesRow>
                        <Badge tag="div" variant="filledGreen">
                          {card.badge}
                        </Badge>
                      </Card.AccessoriesRow>
                    </Card.Accessories>
                    <Card.Copy>
                      <Card.Description>{card.description}</Card.Description>
                    </Card.Copy>
                  </Card.Body>
                </CardBase>
              ))}
            </ColumnTrack>
            <Media lessThan="lg">
              <ShowMore seeMore={seeMore}>
                <ButtonText tag="a" onClick={() => setSeeMore(true)} iconEnd={ChevronRightIcon}>
                  See more deals
                </ButtonText>
              </ShowMore>
            </Media>
          </Body>
          <Caption>
            <Description variant="pMedium" tag="p">
              {captionDescription}
            </Description>
          </Caption>
        </Section.Contents>
      </StyledSection>
    </Wrapper>
  )
}

const Logo = styled(Image)`
  object-fit: contain;
  width: auto;
  max-height: 32px;
  margin: 0;
`

const Wrapper = styled.div`
  max-width: 1280px;
  margin: 0 auto;
`

const StyledSection = styled(SectionBase)`
  ${Section.Wrapper} {
    padding-top: 40px;
  }
`

const Body = styled.div`
  max-width: 1020px;
  padding-bottom: 80px;
  margin: 0 auto;
`

const Caption = styled.div`
  padding-top: 20px;
  text-align: center;
  p {
    margin: 0;
  }
`

const Description = styled(Typography)`
  color: ${({ theme }) => theme.palette.brand.darkAcai[`500`]};
`

interface BaseCardsGridProps {
  seeMore: boolean
}

const ShowMore = styled.div<BaseCardsGridProps>`
  margin: 0 auto;
  padding-top: 80px;
  width: 100%;
  text-align: center;
  ${({ theme }) => theme.breakpoints.down(`md`)} {
    display: ${({ seeMore }) => (seeMore ? `none` : `block`)};
  }
`

export default GetExclusiveDeals
